import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dashboardAPI } from '../../services/api';
import Card, { StatCard } from '../ui/Card';
import Spinner from '../ui/Spinner';
import Alert from '../ui/Alert';
import EmptyState from '../ui/EmptyState';

const BuildingIcon = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
    />
  </svg>
);
const UsersIcon = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
    />
  </svg>
);
const AlertIcon = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
    />
  </svg>
);

// Mini-semafor: bară cu proporțiile verde/galben/roșu per firmă.
const SemaforBar = ({ green, yellow, red, total }) => {
  if (!total) return <span className="text-slate-400 text-xs">—</span>;
  const pct = (n) => `${(n / total) * 100}%`;
  return (
    <div className="flex h-2.5 w-full min-w-[80px] rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800">
      {green > 0 && <div className="bg-emerald-500" style={{ width: pct(green) }} />}
      {yellow > 0 && <div className="bg-amber-500" style={{ width: pct(yellow) }} />}
      {red > 0 && <div className="bg-red-500" style={{ width: pct(red) }} />}
    </div>
  );
};

const PortfolioDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await dashboardAPI.getPortfolio();
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <Spinner size="lg" label={t('portfolioDashboard.loading')} />;
  if (error) return <Alert type="error">{error}</Alert>;
  if (!data) return null;

  const { totals, clients } = data;

  const complianceColor = (p) =>
    p === null
      ? 'text-slate-400 dark:text-slate-500'
      : p >= 90
        ? 'text-emerald-600 dark:text-emerald-300'
        : p >= 70
          ? 'text-amber-600 dark:text-amber-300'
          : 'text-red-600 dark:text-red-300';

  return (
    <div className="space-y-6" data-onboarding="portfolio-dashboard">
      {/* Total agregat — StatCard „Fișe în așteptare" scos momentan: logica de counter
          nu mai e fiabilă (date complicate, multi-slot per fișă). Card-ul amber „Ai semnături
          de făcut" de pe Dashboard rămâne signpost-ul pentru acțiune. */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          label={t('portfolioDashboard.clients')}
          value={totals.clientCount}
          icon={BuildingIcon}
        />
        <StatCard
          label={t('portfolioDashboard.totalEmployees')}
          value={totals.totalEmployees}
          icon={UsersIcon}
        />
        <StatCard
          label={t('portfolioDashboard.ipExpiredCross')}
          value={totals.ipExpired}
          icon={AlertIcon}
        />
      </div>

      {/* Listă clienți */}
      <Card padding={false}>
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            {t('portfolioDashboard.clientPortfolio')}
          </h3>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {t('portfolioDashboard.clickHint')}
          </span>
        </div>

        {clients.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title={t('portfolioDashboard.noClients')}
              description={t('portfolioDashboard.noClientsDesc')}
            />
          </div>
        ) : (
          <>
            {/* MOBILE: card per client */}
            <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800">
              {clients.map((c) => (
                <button
                  key={`mob-${c.companyId}`}
                  onClick={() => navigate(`/companies/${c.companyId}/compliance`)}
                  className="w-full text-left p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <p className="font-semibold text-slate-900 dark:text-slate-100">
                      {c.companyName}
                    </p>
                    <span
                      className={`text-sm font-bold tabular-nums ${complianceColor(c.compliancePercent)}`}
                    >
                      {c.compliancePercent === null ? '—' : `${c.compliancePercent}%`}
                    </span>
                  </div>
                  <SemaforBar
                    green={c.ipUpToDate}
                    yellow={c.ipExpiringSoon}
                    red={c.ipExpired}
                    total={c.ipTotal}
                  />
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500 dark:text-slate-400">
                    <span>
                      {t('portfolioDashboard.employeesCount', { count: c.employeeCount })}
                    </span>
                    {c.ipExpired > 0 && (
                      <span className="text-red-600 dark:text-red-300">
                        {t('portfolioDashboard.expiredCount', { count: c.ipExpired })}
                      </span>
                    )}
                    {c.pendingSheets > 0 && (
                      <span>{t('portfolioDashboard.sheetsCount', { count: c.pendingSheets })}</span>
                    )}
                  </div>
                  <p className="text-[10px] text-primary-600 dark:text-primary-300 mt-2 font-medium">
                    {t('portfolioDashboard.viewDetailedCompliance')} →
                  </p>
                </button>
              ))}
            </div>

            {/* DESKTOP: tabel */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                    <th className="py-3 px-4 font-medium">{t('portfolioDashboard.colClient')}</th>
                    <th className="py-3 px-4 font-medium text-center">
                      {t('portfolioDashboard.colEmployees')}
                    </th>
                    <th className="py-3 px-4 font-medium text-center">
                      {t('portfolioDashboard.colCompliance')}
                    </th>
                    <th className="py-3 px-4 font-medium w-40">
                      {t('portfolioDashboard.colStatus')}
                    </th>
                    <th className="py-3 px-4 font-medium text-center text-amber-700 dark:text-amber-300">
                      {t('portfolioDashboard.colSoon')}
                    </th>
                    <th className="py-3 px-4 font-medium text-center text-red-700 dark:text-red-300">
                      {t('portfolioDashboard.colExpired')}
                    </th>
                    <th className="py-3 px-4 font-medium text-center">
                      {t('portfolioDashboard.colSheets')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr
                      key={c.companyId}
                      onClick={() => navigate(`/companies/${c.companyId}/compliance`)}
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                      title={t('portfolioDashboard.rowClickHint')}
                    >
                      <td className="py-3 px-4 font-medium text-slate-900 dark:text-slate-100 hover:text-primary-600 dark:hover:text-primary-300">
                        {c.companyName}
                      </td>
                      <td className="py-3 px-4 text-center tabular-nums">{c.employeeCount}</td>
                      <td
                        className={`py-3 px-4 text-center tabular-nums font-semibold ${complianceColor(c.compliancePercent)}`}
                      >
                        {c.compliancePercent === null ? '—' : `${c.compliancePercent}%`}
                      </td>
                      <td className="py-3 px-4">
                        <SemaforBar
                          green={c.ipUpToDate}
                          yellow={c.ipExpiringSoon}
                          red={c.ipExpired}
                          total={c.ipTotal}
                        />
                      </td>
                      <td className="py-3 px-4 text-center tabular-nums text-amber-700 dark:text-amber-300">
                        {c.ipExpiringSoon}
                      </td>
                      <td className="py-3 px-4 text-center tabular-nums text-red-700 dark:text-red-300 font-semibold">
                        {c.ipExpired}
                      </td>
                      <td className="py-3 px-4 text-center tabular-nums">{c.pendingSheets}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default PortfolioDashboard;
